import React from 'react';
import {
  renderCard,
  getPlayerStatusInfo,
  formatChips,
  EmptyPlayerSlot,
  PokerActionButtons,
  GameInfoDisplay,
  P2PStatusDisplay
} from '../GameUIHelpers';
import {
  DesktopLeaderboard
} from '../GameStatsHelpers';

// =============================================================================
// GAME TABLE COMPONENTS
// =============================================================================

/**
 * Players List Component for Desktop
 */
export const PlayersList = ({
  players,
  myPlayerId,
  gameState,
  gameInProgress,
  timeoutWarnings
}: {
  players: any[];
  myPlayerId: string;
  gameState: any;
  gameInProgress: boolean;
  timeoutWarnings: Set<string>;
}) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
    <div className="border-b border-gray-100 px-6 py-4">
      <h2 className="text-lg font-semibold text-gray-900">
        Players
      </h2>
      <p className="text-sm text-gray-500">{players.length} active</p>
    </div>
    <div className="p-6">
      <div className="space-y-3">
        {players.map((player, index) => {
          const statusInfo = getPlayerStatusInfo(player, gameState, myPlayerId, timeoutWarnings);
          const isMe = player.id === myPlayerId;
          const isCurrentTurn = statusInfo.isCurrentTurn && !statusInfo.isFolded && statusInfo.isConnected;
          
          return (
            <div 
              key={player.id} 
              className={`relative rounded-xl border transition-all ${
                isMe ? 'bg-blue-50 border-blue-200' : 
                isCurrentTurn ? 'bg-green-50 border-green-200 ring-2 ring-green-100' :
                'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold text-sm ${
                      isMe ? 'bg-blue-500 text-white' :
                      isCurrentTurn ? 'bg-green-500 text-white' :
                      statusInfo.isConnected ? 'bg-gray-600 text-white' : 'bg-gray-400 text-white'
                    }`}>
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className={`font-medium ${
                          isMe ? 'text-blue-900' : 
                          isCurrentTurn ? 'text-green-900' : 'text-gray-900'
                        }`}>
                          {player.name}
                        </p>
                        {isMe && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            You
                          </span>
                        )}
                        {isCurrentTurn && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Turn
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 text-xs text-gray-600 mt-1">
                        <span className="font-medium">${formatChips(player.chips || 0)}</span>
                        {gameInProgress && player.currentBet > 0 && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span>Bet: ${formatChips(player.currentBet || 0)}</span>
                          </>
                        )}
                        {statusInfo.isFolded && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-red-500 font-medium">Folded</span>
                          </>
                        )}
                        {!statusInfo.isConnected && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-red-500 font-medium">Offline</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    statusInfo.isConnected ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                </div>
              </div>
              {isCurrentTurn && (
                <div className="absolute inset-0 rounded-xl bg-green-500/5 pointer-events-none"></div>
              )}
            </div>
          );
        })}
        
        {/* Show empty slots */}
        {Array.from({ length: Math.max(0, 6 - players.length) }, (_, i) => (
          <div key={`empty-${i}`} className="rounded-xl border-2 border-dashed border-gray-200 p-4">
            <div className="flex items-center space-x-3 opacity-40">
              <div className="w-10 h-10 rounded-xl bg-gray-200"></div>
              <div>
                <div className="text-sm text-gray-500">Empty seat</div>
                <div className="text-xs text-gray-400">Waiting for player</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Game Table Component for Desktop
 */
export const GameTable = ({
  gameInProgress,
  gameState,
  holeCards,
  showdownResults,
  currentPlayer,
  players,
  myPlayerId,
  canCheck,
  callAmount,
  p2pGameMode,
  mentalPokerPhase,
  onPokerAction,
  onRebuy,
  onDismissShowdown
}: {
  gameInProgress: boolean;
  gameState: any;
  holeCards: string[];
  showdownResults: any;
  currentPlayer: any;
  players: any[];
  myPlayerId: string;
  canCheck: boolean;
  callAmount: number;
  p2pGameMode: boolean;
  mentalPokerPhase: string;
  onPokerAction: (action: string, amount?: number) => void;
  onRebuy: () => void;
  onDismissShowdown: () => void;
}) => (
  <div className="md:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm">
    {p2pGameMode && (
      <div className="border-b border-gray-100 px-6 py-3">
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
          <span className="text-purple-600 font-medium">P2P Mode</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">{mentalPokerPhase}</span>
        </div>
      </div>
    )}
    
    <div className="p-8">
      <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-12 min-h-[480px] flex items-center justify-center border border-emerald-200">
        <div className="text-center w-full max-w-4xl">
          
          {gameInProgress && gameState ? (
            <>
              {/* Community Cards */}
              <div className="mb-8">
                <div className="text-sm font-medium text-gray-600 mb-4 uppercase tracking-wide">
                  {gameState.round} • Community Cards
                </div>
                <div className="flex justify-center items-center min-h-[100px]">
                  {gameState.communityCards && gameState.communityCards.length > 0 ? (
                    gameState.communityCards.map((card: string, index: number) => 
                      renderCard(card, 'normal', index)
                    )
                  ) : (
                    <div className="text-gray-500 text-center bg-white/50 rounded-lg px-6 py-4 border border-dashed border-gray-300">
                      {gameState.round === 'preflop' ? 'No community cards yet' : 'Dealing cards...'}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Your Hole Cards */}
              <div className="mb-8">
                <div className="text-sm font-medium text-gray-600 mb-4 uppercase tracking-wide">
                  Your Hand
                </div>
                <div className="flex justify-center">
                  {holeCards && holeCards.length > 0 ? (
                    holeCards.map((card, index) => renderCard(card, 'normal', index))
                  ) : (
                    <div className="text-gray-500 bg-white/50 rounded-lg px-6 py-4 border border-dashed border-gray-300">
                      No cards dealt
                    </div>
                  )}
                </div>
              </div>
              
              {/* Game Info */}
              <GameInfoDisplay 
                gameState={gameState}
                callAmount={callAmount}
                currentPlayer={currentPlayer}
              />
              
              {/* Action Buttons - Only show if it's the player's turn */}
              {gameState.currentPlayerTurn === myPlayerId ? (
                <PokerActionButtons 
                  onAction={onPokerAction}
                  canCheck={canCheck}
                  callAmount={callAmount}
                  gameState={gameState}
                  currentPlayer={currentPlayer}
                  p2pGameMode={p2pGameMode}
                />
              ) : (
                <div className="text-center">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-4 inline-block">
                    <p className="text-amber-700 font-medium">
                      Waiting for {players.find(p => p.id === gameState.currentPlayerTurn)?.name || 'player'} to act
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : players.length < 2 ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">♠</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Waiting for players</h3>
              <p className="text-gray-600">
                {players.length} / 2 players minimum to start
              </p>
            </div>
          ) : (
            <WaitingForGameStart 
              players={players}
              currentPlayer={currentPlayer}
              onRebuy={onRebuy}
              p2pGameMode={p2pGameMode}
            />
          )}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Waiting for Game Start Component
 */
export const WaitingForGameStart = ({
  players,
  currentPlayer,
  onRebuy,
  p2pGameMode
}: {
  players: any[];
  currentPlayer: any;
  onRebuy: () => void;
  p2pGameMode: boolean;
}) => {
  const playersWithChips = players.filter(p => (p.chips || 0) > 0 && (p.isConnected === true || p.isConnected === undefined));
  
  if (playersWithChips.length < 2) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <span className="text-amber-600 text-2xl">⏳</span>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Need more players</h3>
        <p className="text-gray-600 mb-6">
          {playersWithChips.length} / 2 players with chips
          {players.length - playersWithChips.length > 0 && 
            ` • ${players.length - playersWithChips.length} need to rebuy`
          }
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-4 inline-block">
          <p className="text-amber-700 font-medium">Waiting for players</p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <span className="text-green-600 text-2xl">🎮</span>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to play!</h3>
      <p className="text-gray-600 mb-6">
        {playersWithChips.length} players ready • Game starts when someone clicks "Start Game"
      </p>
      <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
        <div className={`w-1.5 h-1.5 rounded-full ${p2pGameMode ? 'bg-purple-500' : 'bg-blue-500'}`}></div>
        <span>{p2pGameMode ? 'P2P Mode' : 'Server Mode'}</span>
      </div>
    </div>
  );
};

/**
 * Game Header Component
 */
export const GameHeader = ({
  roomId,
  p2pGameMode,
  gameStarted,
  players,
  connectionStatus,
  p2pStatus,
  showLeaderboard,
  onToggleLeaderboard,
  onLeaveGame,
  currentPlayer,
  gameState,
  onRebuy
}: {
  roomId: string;
  p2pGameMode: boolean;
  gameStarted: boolean;
  players: any[];
  connectionStatus: string;
  p2pStatus: any;
  showLeaderboard: boolean;
  onToggleLeaderboard: () => void;
  onLeaveGame: () => void;
  currentPlayer: any;
  gameState: any;
  onRebuy: () => void;
}) => {
  return (
    <header className="bg-white border-b border-gray-100">
    {/* P2P Development Branch Indicator */}
    <div className="bg-gradient-to-r from-purple-500 to-blue-600 text-white text-center py-2 px-4">
      <div className="flex items-center justify-center space-x-2">
        <span className="text-lg">🚀</span>
        <span className="font-semibold">P2P DEVELOPMENT BRANCH</span>
        <span className="text-sm opacity-90">- Distributed Game State Active</span>
        {p2pGameMode && (
          <span className="bg-white/20 px-2 py-1 rounded text-xs">
            🌐 CONSENSUS MODE
          </span>
        )}
      </div>
    </div>
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <span className="text-white text-lg">♠</span>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{roomId}</h1>
            <div className="flex items-center space-x-3 text-xs text-gray-500">
              <span>{players.length} players</span>
              {p2pGameMode && (
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span>P2P</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Leaderboard Toggle Button */}
          {gameStarted && players.length > 0 && (
            <button
              onClick={() => {
                console.log('🏆 LEADERBOARD BUTTON CLICKED!', { 
                  gameStarted, 
                  playersCount: players.length,
                  showLeaderboard 
                });
                onToggleLeaderboard();
                console.log('📊 After toggle - showLeaderboard should be:', !showLeaderboard);
              }}
              className="h-9 px-4 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium transition-all flex items-center space-x-2"
              title="Toggle Leaderboard"
            >
              <span className="text-base">🏆</span>
              <span className="hidden sm:inline">Stats</span>
            </button>
          )}
          
          {/* Rebuy Button */}
          {(!gameState?.currentPlayerTurn || gameState.currentPlayerTurn !== currentPlayer?.id) && (currentPlayer?.gameRebuys || 0) < 5 && (
            <button
              onClick={onRebuy}
              className="h-9 px-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium transition-all flex items-center space-x-2"
              title={`Rebuy for $1000 (${(currentPlayer?.gameRebuys || 0)}/5)`}
            >
              <span className="text-base">+</span>
              <span className="hidden sm:inline">Rebuy</span>
            </button>
          )}
          
          <P2PStatusDisplay 
            p2pStatus={p2pStatus}
            p2pGameMode={p2pGameMode}
            connectionStatus={connectionStatus}
          />
          
          <button
            onClick={onLeaveGame}
            className="h-9 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-sm font-medium transition-all"
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  </header>
  );
};

/**
 * Showdown Results Component
 */
export const ShowdownResults = ({
  showdownResults,
  onDismiss
}: {
  showdownResults: any;
  onDismiss: () => void;
}) => {
  if (!showdownResults) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm max-w-2xl w-full">
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl px-6 py-4">
        <h2 className="text-white text-xl font-semibold text-center flex items-center justify-center space-x-2">
          <span>🎉</span>
          <span>{showdownResults.type === 'showdown' ? 'Showdown Results' : 'Game Over'}</span>
          <span>🎉</span>
        </h2>
      </div>
      
      <div className="p-6">
        {showdownResults.type === 'showdown' ? (
          <>
            {/* Winner Information */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center">
              {showdownResults.winners.length === 1 ? (
                <div>
                  <div className="text-green-800 text-lg font-semibold mb-2">
                    🏆 Winner: {showdownResults.winners[0].name}
                  </div>
                  <div className="text-green-700 mb-2">{showdownResults.winningHand.name}</div>
                  <div className="text-green-900 text-xl font-bold">Wins ${showdownResults.pot}</div>
                </div>
              ) : (
                <div>
                  <div className="text-green-800 text-lg font-semibold">Split Pot!</div>
                  <div className="text-green-700 text-sm">{showdownResults.winningHand.name}</div>
                  <div className="text-green-900 font-bold">${showdownResults.pot}</div>
                </div>
              )}
            </div>

            {/* Final Board */}
            {showdownResults.communityCards && (
              <div className="mb-6">
                <h4 className="text-gray-800 font-semibold mb-3 text-center">Final Board</h4>
                <div className="flex justify-center gap-2">
                  {showdownResults.communityCards.map((card: string, i: number) => 
                    renderCard(card, 'showdownNormal', i)
                  )}
                </div>
              </div>
            )}

            {/* All Player Hands */}
            {showdownResults.allHands && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h4 className="text-gray-800 font-semibold mb-3 text-center">All Hands</h4>
                <div className="space-y-3">
                  {showdownResults.allHands.map((hand: any, index: number) => (
                    <div key={index} className="flex justify-between items-center bg-white rounded-xl p-3 border border-gray-200">
                      <div>
                        <div className="text-gray-900 font-medium">{hand.playerName}</div>
                        <div className="text-gray-600 text-sm">{hand.bestHand.name}</div>
                      </div>
                      <div className="flex gap-1">
                        {hand.holeCards.map((card: string, i: number) => 
                          renderCard(card, 'showdownSmall', i)
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center mb-6">
            <div className="text-gray-900 text-lg font-semibold">Winner: {showdownResults.winners[0]?.name}</div>
            <div className="text-gray-600">{showdownResults.winningReason}</div>
          </div>
        )}
        
        <div className="text-center">
          <button 
            onClick={onDismiss}
            className="h-10 px-6 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
          >
            Continue Playing
          </button>
        </div>
      </div>
    </div>
  );
};